import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CustomOrderPage.css';

const FLAVORS = [
  'Nice', 'Strawberry', 'Vanilla', 'Coconut', 'Chocolate', 'Lemon', 'Red Velvet', 'Caramel', 'Pineapple', 'Mango', 'Blueberry'
];
const ICINGS = ['Butter', 'Fondant', 'Royal'];
const GOOGLE_API_KEY = 'AIzaSyDSMqKFP8eIfLrStrOoGIG_eqeMeNOcKLw';

function DeliveryPlaceAutocomplete({ value, onChange }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  React.useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    setShowDropdown(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!val) {
        setSuggestions([]);
        setLoading(false);
        return;
      }
      if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.fetchSuggestion) {
        setLoading(true);
        window.google.maps.places.fetchSuggestion({ input: val })
          .then((response) => {
            setLoading(false);
            if (response && Array.isArray(response.suggestions)) {
              setSuggestions(response.suggestions);
            } else {
              setSuggestions([]);
            }
          })
          .catch(() => {
            setLoading(false);
            setSuggestions([]);
          });
      }
    }, 300);
  };

  const handleSelect = (desc) => {
    setInputValue(desc);
    onChange(desc);
    setSuggestions([]);
    setShowDropdown(false);
  };

  return (
    <div className="autocomplete-root" style={{ position: 'relative' }}>
      <input
        value={inputValue}
        onChange={handleChange}
        placeholder="Start typing address..."
        className="autocomplete-input"
        autoComplete="off"
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
      />
      {showDropdown && suggestions.length > 0 && (
        <div className="autocomplete-dropdown">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.placeId || suggestion.place_id || suggestion.description}
              className="autocomplete-item"
              onMouseDown={() => handleSelect(suggestion.description)}
            >
              {suggestion.description}
            </div>
          ))}
        </div>
      )}
      {loading && <div className="autocomplete-dropdown">Loading...</div>}
    </div>
  );
}

export default function CustomOrderPage({ currentUser, onOrderCreated }) {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [deliveryPlace, setDeliveryPlace] = useState('');
  const [flavors, setFlavors] = useState([]);
  const [icing, setIcing] = useState('');
  const [cakeColor, setCakeColor] = useState('');
  const [cakePrice, setCakePrice] = useState('');
  const [cakeWritings, setCakeWritings] = useState('');
  const [description, setDescription] = useState('');
  const [pickDate, setPickDate] = useState('');
  const [pickTime, setPickTime] = useState('');
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [localUser, setLocalUser] = useState(currentUser || null);

  useEffect(() => {
    if (!currentUser) {
      const savedUser = localStorage.getItem('user');
      if (savedUser) setLocalUser(JSON.parse(savedUser));
    }
  }, [currentUser]);

  // Handle image uploads (multi)
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    // Merge new files with existing ones, avoiding duplicates by name + size
    const merged = [...images];
    files.forEach(file => {
      if (!merged.some(f => f.name === file.name && f.size === file.size)) {
        merged.push(file);
      }
    });
    setImages(merged);
    setPreviewUrls(merged.map(file => URL.createObjectURL(file)));
  };

  const handleFlavorToggle = (flavor) => {
    setFlavors((prev) => prev.includes(flavor) ? prev.filter(f => f !== flavor) : [...prev, flavor]);
  };

  const handleIcingChange = (e) => setIcing(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!customer || !customerNumber || !deliveryPlace || flavors.length === 0 || !icing || !cakeColor || !cakePrice || !pickDate || !pickTime || !cakeWritings) {
      setError('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      // Upload images
      let imageUrls = [];
      if (images.length > 0) {
        const formData = new FormData();
        images.forEach((img, i) => formData.append('images', img));
        const imgRes = await fetch('https://devbite.vercel.app/api/upload_images', { method: 'POST', body: formData });
        let data = {};
        try {
          data = await imgRes.json();
        } catch (jsonErr) {
          console.error('Failed to parse image upload response:', jsonErr);
        }
        if (imgRes.ok) {
          imageUrls = data.urls || [];
        } else {
          console.error('Image upload error:', data.error || data);
          setError(data.error || 'Failed to upload images');
          setSubmitting(false);
          return;
        }
      }
      const orderData = {
        customer,
        customerNumber,
        deliveryPlace,
        flavors,
        icing,
        cakeColor,
        cakePrice,
        cakeWritings,
        description,
        pickDate,
        pickTime,
        createdBy: localUser?.uid,
        createdByName: localUser?.displayName || localUser?.name || localUser?.email || '',
        createdByPhoto: localUser?.photoURL || localUser?.photoUrl || '',
        images: imageUrls,
        customerorder: true,
        verified: false,
        company: 'cakedoctor'
      };
      const res = await fetch('https://devbite.vercel.app/custom_order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      if (!res.ok) throw new Error('Failed to save order');
      if (onOrderCreated) onOrderCreated();
      setSubmitting(false);
      // Navigate to My Orders page on success
      window.location.href = '/myorders';
      // alert('Order created successfully!');
    } catch (err) {
      setError(err.message || 'Failed to create order');
      setSubmitting(false);
    }
  };

  return (
    <div className="custom-order-bg">
      <header className="custom-order-appbar">
        <button className="custom-order-back" onClick={() => navigate(-1)}>&larr;</button>
        <span className="custom-order-appbar-title">Custom Order</span>
      </header>
      <div className="custom-order-root">
        <h2>Create Custom Order</h2>
        <form className="custom-order-form" onSubmit={handleSubmit}>
          <label>Customer Name*</label>
          <input value={customer} onChange={e => setCustomer(e.target.value)} required />
          <label>Customer Number*</label>
          <input value={customerNumber} onChange={e => setCustomerNumber(e.target.value)} required />
          <label>Delivery Place*</label>
          <DeliveryPlaceAutocomplete value={deliveryPlace} onChange={setDeliveryPlace} />
          <label>Flavors*</label>
          <div className="flavors-list">
            {FLAVORS.map(f => (
              <label key={f}><input type="checkbox" checked={flavors.includes(f)} onChange={() => handleFlavorToggle(f)} /> {f}</label>
            ))}
          </div>
          <label>Icing*</label>
          <select value={icing} onChange={handleIcingChange} required>
            <option value="">Select Icing</option>
            {ICINGS.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <label>Cake Color*</label>
          <input value={cakeColor} onChange={e => setCakeColor(e.target.value)} required />
          <label>Cake Price*</label>
          <input value={cakePrice} onChange={e => setCakePrice(e.target.value)} required />
          <label>Cake Writings*</label>
          <input value={cakeWritings} onChange={e => setCakeWritings(e.target.value)} required />
          <label>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} />
          <label>Pick Date*</label>
          <input type="date" value={pickDate} onChange={e => setPickDate(e.target.value)} required />
          <label>Pick Time*</label>
          <input type="time" value={pickTime} onChange={e => setPickTime(e.target.value)} required />
          <label>Images</label>
          <input type="file" multiple accept="image/*" onChange={handleImageChange} />
          {previewUrls.length > 0 && (
            <div className="image-preview-grid">
              {previewUrls.map((url, idx) => (
                <div className="image-preview-item" key={idx}>
                  <img src={url} alt={`preview-${idx}`} className="image-preview-img" />
                </div>
              ))}
            </div>
          )}
          {error && <div className="form-error">{error}</div>}
          <button type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Order'}</button>
        </form>
      </div>
    </div>
  );
}
